import React, { createContext, useContext, useEffect, useReducer, useCallback, useRef, ReactNode } from 'react';
import { buildSupabaseHeaders, supabase, isMockMode } from '@/lib/supabase';
import { getValidAccessToken } from '@/lib/auth/session';
import { PlayerStats, ProgressStatus, AnswerResult } from '@/types';
import { useAuth } from './AuthContext';

interface GameState {
    stats: PlayerStats | null;
    progress: Record<string, ProgressStatus>;
    badges: string[];
    loading: boolean;
    levelUpPending: boolean;
    newBadge: string | null;
    rank: number | null;
    globalStats: {
        players: number;
    } | null;
}

type GameAction =
    | { type: 'LOAD_SUCCESS'; payload: Partial<GameState> }
    | { type: 'UPDATE_STATS'; payload: Partial<PlayerStats> }
    | { type: 'UPDATE_PROGRESS'; payload: Record<string, ProgressStatus> }
    | { type: 'AWARD_BADGE'; payload: string }
    | { type: 'TRIGGER_LEVEL_UP' }
    | { type: 'DISMISS_LEVEL_UP' }
    | { type: 'DISMISS_BADGE' };

interface GameContextType extends GameState {
    submitAnswer: (questionId: string, answer: any, timeTakenS: number) => Promise<AnswerResult>;
    dismissLevelUp: () => void;
    dismissBadge: () => void;
    dispatch: React.Dispatch<GameAction>;
    refetch: () => Promise<void>;
}

const GameContext = createContext<GameContextType | null>(null);

const MOCK_STATS: PlayerStats = {
    xp: 340,
    level: 5,
    lives: 3,
    max_lives: 3,
    last_life_regen: new Date().toISOString(),
    streak_days: 4,
    last_active: new Date().toISOString().slice(0, 10),
    handle: 'MockPlayer',
};

const MOCK_PROGRESS: Record<string, ProgressStatus> = {
    'what-is-git': 'completed',
    'git-setup': 'completed',
    'staging-area': 'in_progress',
};

const initialState: GameState = {
    stats: null,
    progress: {},
    badges: [],
    loading: true,
    levelUpPending: false,
    newBadge: null,
    rank: null,
    globalStats: null,
};

function gameReducer(state: GameState, action: GameAction): GameState {
    switch (action.type) {
        case 'LOAD_SUCCESS':
            return { ...state, ...action.payload, loading: false };
        case 'UPDATE_STATS':
            return { ...state, stats: state.stats ? { ...state.stats, ...action.payload } : null };
        case 'UPDATE_PROGRESS':
            return { ...state, progress: { ...state.progress, ...action.payload } };
        case 'AWARD_BADGE':
            return { ...state, badges: [...state.badges, action.payload], newBadge: action.payload };
        case 'TRIGGER_LEVEL_UP':
            return { ...state, levelUpPending: true };
        case 'DISMISS_LEVEL_UP':
            return { ...state, levelUpPending: false };
        case 'DISMISS_BADGE':
            return { ...state, newBadge: null };
        default:
            return state;
    }
}

export function GameProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [state, dispatch] = useReducer(gameReducer, initialState);
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        fetchGlobalStats();
    }, []);

    async function fetchGlobalStats() {
        if (!supabase) return;
        try {
            const { data, error } = await supabase.functions.invoke('get-global-stats');
            if (error) throw error;
            dispatch({ type: 'LOAD_SUCCESS', payload: { globalStats: data } });
        } catch (error) {
            console.error('Error fetching global stats:', error);
        }
    }

    const loadGameState = useCallback(async function loadGameState() {
        if (!user || isMockMode || !supabase) {
            if (isMockMode || (!supabase && user)) {
                dispatch({
                    type: 'LOAD_SUCCESS',
                    payload: {
                        stats: MOCK_STATS,
                        progress: MOCK_PROGRESS,
                        badges: ['first-spawn'],
                    },
                });
            }
            return;
        }

        try {
            const accessToken = await getValidAccessToken();
            if (!accessToken) {
                throw new Error('Session expired. Please sign in again.');
            }

            // 2. Load stats
            const { data: statsData, error: statsError } = await supabase
                .from('player_stats')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle();

            if (statsError) {
                throw statsError;
            }

            // Default stats for new player if none exists
            const stats = statsData || {
                xp: 0,
                level: 1,
                lives: 5,
                max_lives: 5,
                streak_days: 0,
                last_active: new Date().toISOString().slice(0, 10),
            };

            // 3. Load progress
            const { data: progressData } = await supabase.from('user_progress').select('module_id, status, modules(slug)').eq('user_id', user.id);
            const progress: Record<string, ProgressStatus> = {};
            progressData?.forEach((p: any) => {
                if (p.modules?.slug) progress[p.modules.slug] = p.status;
            });

            // 4. Load badges
            const { data: badgeData } = await supabase.from('user_badges').select('badge_id').eq('user_id', user.id);
            const badges = badgeData?.map((b: any) => b.badge_id) || [];

            // 5. Check lives regen
            const { data: livesData } = await supabase.functions.invoke('check-lives', {
                method: 'POST',
                headers: buildSupabaseHeaders(accessToken),
            });

            // 6. Get rank via security-definer RPC (bypasses RLS)
            const { data: rankData } = await supabase.rpc('get_player_rank', { p_user_xp: stats.xp });

            dispatch({
                type: 'LOAD_SUCCESS',
                payload: {
                    stats: { ...stats, ...livesData },
                    progress,
                    badges,
                    rank: typeof rankData === 'number' ? rankData : null,
                }
            });
        } catch (error) {
            console.error('Error loading game state:', error);
            const current = stateRef.current;
            dispatch({
                type: 'LOAD_SUCCESS',
                payload: {
                    stats: current.stats ?? {
                        xp: 0,
                        level: 1,
                        lives: 5,
                        max_lives: 5,
                        last_life_regen: new Date().toISOString(),
                        streak_days: 0,
                        last_active: new Date().toISOString().slice(0, 10),
                    },
                    progress: current.progress,
                    badges: current.badges,
                    rank: current.rank,
                },
            });
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        loadGameState();
    }, [user, loadGameState]);

    const submitAnswer = useCallback(async (questionId: string, answer: any, timeTakenS: number): Promise<AnswerResult> => {
        if (!user || isMockMode || !supabase) {
            const correct = Math.random() > 0.3;
            const currentLives = stateRef.current.stats?.lives ?? 1;
            const livesAfter = correct ? currentLives : Math.max(0, currentLives - 1);
            if (!correct) dispatch({ type: 'UPDATE_STATS', payload: { lives: livesAfter } });
            return {
                correct,
                xp_awarded: correct ? 20 : 0,
                lives_remaining: livesAfter,
                level_up: false,
                badges_earned: [],
            };
        }

        const accessToken = await getValidAccessToken();
        if (!accessToken) {
            throw new Error('Session expired. Please sign in again.');
        }

        const { data, error } = await supabase.functions.invoke('submit-answer', {
            body: {
                user_id: user.id,
                question_id: questionId,
                answer,
            },
            headers: buildSupabaseHeaders(accessToken),
        });

        if (error) throw error;

        // Sync local state with authoritative server result
        if (data.correct) {
            dispatch({
                type: 'UPDATE_STATS',
                payload: { xp: (stateRef.current.stats?.xp || 0) + data.xp_awarded }
            });
            if (data.level_up) dispatch({ type: 'TRIGGER_LEVEL_UP' });
        } else {
            dispatch({ type: 'UPDATE_STATS', payload: { lives: data.lives_remaining } });
        }

        return data;
    }, [user]);

    const dismissLevelUp = () => dispatch({ type: 'DISMISS_LEVEL_UP' });
    const dismissBadge = () => dispatch({ type: 'DISMISS_BADGE' });

    return (
        <GameContext.Provider value={{ ...state, submitAnswer, dismissLevelUp, dismissBadge, dispatch, refetch: loadGameState }}>
            {children}
        </GameContext.Provider>
    );
}

export function useGame() {
    const ctx = useContext(GameContext);
    if (!ctx) throw new Error('useGame must be used within GameProvider');
    return ctx;
}
